export interface FormDetailsData {
    formVersion?: string,
    formType?: string,
}

/**
 * FormDetails is a Singleton object used for storage and retrival of form details
 */
export class FormDetails {
  private static instance: FormDetails;

  data: FormDetailsData = { formVersion: '', formType: '' };

  /**
     * private to prevent direct construction calls with the `new` operator.
     */
  private constructor() { // eslint-disable-line 
  }

  /**
     * The static method that controls the access to the singleton
     */
  public static getInstance(): FormDetails {
    if (!FormDetails.instance) {
      FormDetails.instance = new FormDetails();
    }

    return FormDetails.instance;
  }

  /**
     * Get the FormData which includes type and version
     */
  public getData(): FormDetailsData {
    // return a copy the details object to prevent accidental mutation
    return { ...this.data };
  }

  /**
 * Set the FormData which includes type and version
 */
  public setFormType(formType: string | undefined | null) {
    this.data.formType = formType || '';
  }

  /**
     * Set the FormData which includes type and version
     */
  public setFormVersion(formVersion: string | undefined | null) {
    this.data.formVersion = formVersion || '';
  }
}

export interface AppToggleState {
    errorDebugger: boolean,
    userActivity: boolean,
}
/**
 * AppKeydownWatchers is a Singleton object used for storage and retrival of special states
 */
export class AppKeydownWatchers {
  private static instance: AppKeydownWatchers;

  private errorKeyWatcherActive = false;

  private userInteractionKeyWatcherActive = false;

  private userInteractionMouseWatcherActive = false;

  /**
     *  Adds a watcher to toggele the errorDebugger when ctrl+alt+e is pressed.
     */
  private errorKeyWatcher = (e: KeyboardEvent) => {
    if (e.ctrlKey && e.altKey && (e.key === 'e' || e.key === 'E') && !e.shiftKey) { // case sensitive
      this.toggleErrorDebugger();
    }
  };

  /**
     *  Adds a watcher to set the User Activity to true when buttons are pressed.
     */
  private userActivityKeyWatcher = (e: KeyboardEvent) => {
    this.updateUserActivity();
  };

  /**
     *  Adds a watcher to set the User Activity to true when mousebuttons are pressed.
     */
  private userActivityMouseWatcher = (e: MouseEvent) => {
    this.updateUserActivity();
  };

  data: AppToggleState = { errorDebugger: false, userActivity: false };

  /**
     * private to prevent direct construction calls with the `new` operator.
     */
  private constructor() { // eslint-disable-line @typescript-eslint/no-empty-function
    this.errorKeyWatcher = this.errorKeyWatcher.bind(this);
    this.userActivityKeyWatcher = this.userActivityKeyWatcher.bind(this);
    this.userActivityMouseWatcher = this.userActivityMouseWatcher.bind(this);
  }

  /**
     * The static method that controls the access to the singleton
     */
  public static getInstance(): AppKeydownWatchers {
    if (!AppKeydownWatchers.instance) {
      AppKeydownWatchers.instance = new AppKeydownWatchers();
    }
    return AppKeydownWatchers.instance;
  }

  /**
     * Get the returns the current state of the AppToggleState
     */
  public getAppToggleState(): AppToggleState {
    // return a copy the details object to prevent accidental mutation
    return { ...this.data };
  }

  /**
    * Set the errorDebugger state to on or off
    */
  public toggleErrorDebugger() {
    this.data.errorDebugger = !this.data.errorDebugger;
  }

  /**
    * Updates User activity
    */
  public updateUserActivity() {
    if (!this.data.userActivity) {
      this.data.userActivity = true;
      this.watchUserActivity(false);
    }
  }

  /**
    * Resets User activity
    */
  public restartUserActivity() {
    this.data.userActivity = false;
    this.watchUserActivity(true);
  }

  public watchErrorDebugger(active: boolean) {
    if (active) {
      if (!this.errorKeyWatcherActive) {
        document.addEventListener('keydown', this.errorKeyWatcher);
        this.errorKeyWatcherActive = true;
      }
    } else {
      if (this.errorKeyWatcherActive) {
        document.removeEventListener('keydown', this.errorKeyWatcher);
      }
      this.errorKeyWatcherActive = false;
    }
  }

  public watchUserActivity(active: boolean) {
    if (active) {
      if (!this.userInteractionKeyWatcherActive) {
        document.addEventListener('keydown', this.userActivityKeyWatcher);
        this.userInteractionKeyWatcherActive = true;
      }
      if (!this.userInteractionMouseWatcherActive) {
        document.addEventListener('click', this.userActivityMouseWatcher);
        this.userInteractionMouseWatcherActive = true;
      }
    } else {
      if (this.userInteractionKeyWatcherActive) {
        document.removeEventListener('keydown', this.userActivityKeyWatcher);
      }
      if (this.userInteractionMouseWatcherActive) {
        document.removeEventListener('click', this.userActivityMouseWatcher);
      }
      this.userInteractionKeyWatcherActive = false;
      this.userInteractionMouseWatcherActive = false;
    }
  }
}
